/**
 * Saved Search "+N new" entry from Home (Spec §7, R8).
 *
 * Home's +N pill opens `/workspaces/{id}` with `focusNewResults` state. The
 * Saved Search page highlights the rows behind that count. Which rows are new
 * comes only from the server checkpoint (explicit ids); a bare count never
 * picks rows, so an unavailable feed highlights nothing instead of guessing.
 */
import { FOCUS_NEW_RESULTS_STATE } from "./homeSavedSearchUpdates";

/** How long the new rows flash after they render (designer value, ms). */
export const NEW_RESULT_ROW_FLASH_MS = 4000;

/** Row class while the flash is active (styled in EnrichmentGrid.styles). */
export const HOME_NEW_RESULT_FLASH_CLASS = "home-new-result-flash";

/** Read a +N entry from navigation state, or null when it is not one. */
export function readFocusNewResultsState(state) {
  if (!state || typeof state !== "object" || !state[FOCUS_NEW_RESULTS_STATE]) return null;
  const newCompanyIds = Array.isArray(state.newCompanyIds)
    ? state.newCompanyIds.map(String).filter(Boolean)
    : [];
  const count = Number(state.newCompanyCount);
  const newCompanyCount = newCompanyIds.length > 0
    ? newCompanyIds.length
    : (Number.isFinite(count) && count > 0 ? count : 0);
  return newCompanyCount > 0 ? { newCompanyIds, newCompanyCount } : null;
}

function companyRowId(company) {
  if (!company || typeof company !== "object") return null;
  const id = company.stagedId ?? company.alphasearchId ?? company.id;
  return id != null && String(id).trim() !== "" ? String(id) : null;
}

/** The new ids that are on the loaded rows, in the server's order. */
export function resolveNewCompanyIds(companies, newCompanyIds) {
  if (!Array.isArray(companies) || !Array.isArray(newCompanyIds)) return [];
  const present = new Set(companies.map(companyRowId).filter(Boolean));
  return newCompanyIds.map(String).filter((id) => present.has(id));
}

/** Stable reorder: new rows first (in `newCompanyIds` order), then the rest. */
export function prioritizeNewCompanies(companies, newCompanyIds) {
  if (!Array.isArray(companies) || companies.length === 0) return companies;
  if (!Array.isArray(newCompanyIds) || newCompanyIds.length === 0) return companies;
  const rank = new Map(newCompanyIds.map((id, index) => [String(id), index]));
  const head = [];
  const tail = [];
  companies.forEach((company) => {
    const id = companyRowId(company);
    (id != null && rank.has(id) ? head : tail).push(company);
  });
  head.sort((a, b) => rank.get(companyRowId(a)) - rank.get(companyRowId(b)));
  return [...head, ...tail];
}
